import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { MisContent, CustomContentService } from 'src/app/services/custom-content.service';
import { ReportsService } from 'src/app/services/reports.service';
import { Navigation } from 'src/app/model/user';

enum TabIndices {
  CREATE,
  PREVIEW,
  AUDIENCE,
  SCHEDULE,
}

enum NotificationStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  INACTIVE = 'INACTIVE',
}

enum messageLevel {
  SYSTEM = 'SYSTEM',
  COMPANY = 'COMPANY',
  STATUS = 'DISTRIBUTOR',
}

enum DisplayIn {
  VRM_MIS = 'VRM_MIS',
  DRIVER_HQ = 'DRIVER_HQ',
}

enum DisplayAs {
  POST_LOGIN = 'POST_LOGIN',
  BROADCAST = 'BROADCAST',
}

interface PageChildNavItem extends Navigation {
  isChild?: boolean;
  parentName?: string;
  parentTranslatedName?: string;
  parentId?: string;
}

@Component({
  selector: 'custom-content-admin',
  templateUrl: './custom-content-admin.component.html',
  styleUrls: ['./custom-content-admin.component.scss', './mis-notifications-preview.component.scss'],
})
export class CustomContentAdminComponent implements OnInit, OnDestroy {
  contentType: MisContent;
  isTooltipContent: boolean;
  availableVrmPageList: Array<PageChildNavItem> = [];
  availableTooltipPreviews: Array<PageChildNavItem> = [];

  public previewContent: any;
  public misContentForm: UntypedFormGroup;
  public companySpecificLocations: [
    {
      colName: string;
      text: string;
      filters?: { text: string; value: string }[];
      data?: { locationValue: string }[];
      selected?: { locationValue: string }[];
    }
  ];

  public allowedCompanies: any;
  public selectedCompanies: { company: string }[];
  public companyDropdownSettings: any;
  public locationDropdownSettings: any;
  public selectedTabIndex: number;
  public loading: boolean;

  public companyChangeSubscription: Subscription;

  public textTranslations: any = {
    vrmNotification: {
      english: 'VRM NOTIFICATION',
      spanish: 'VRM NOTIFICATION',
    },
    continue: {
      english: 'CONTINUE',
      spanish: 'CONTINUE',
    },
  };

  formRequiredFields: Array<string> = [];

  get tooltips() {
    return this.misContentForm.controls['tooltips'] as UntypedFormArray;
  }

  get message() {
    return this.misContentForm.controls['message'] as UntypedFormGroup;
  }

  get languageVariants() {
    return this.cService.vrmLanguages.filter((language) => {
      return language.country.toLowerCase() !== this.cContentService.defaultLanguage.country;
    });
  }

  get availableVariants() {
    return this.misContentForm.controls['availableVariants'] as UntypedFormGroup;
  }

  get variants() {
    return this.misContentForm.controls['variants'] as UntypedFormArray;
  }

  get distributors() {
    return this.misContentForm.controls['distributors'] as UntypedFormGroup;
  }

  get companies() {
    return this.misContentForm.controls['companies'] as UntypedFormControl;
  }

  get locations() {
    return this.misContentForm.controls['locations'] as UntypedFormGroup;
  }

  get displays() {
    return this.misContentForm.controls['displays'] as UntypedFormGroup;
  }

  get schedules() {
    return this.displays.controls['schedules'] as UntypedFormGroup;
  }

  get isDistributorUser() {
    return this.cService.userProfile.isDistributor;
  }

  get isDistributorCompany() {
    return this.cService.userProfile.distributorUserCompany !== null;
  }

  get distributorUserCompanyName() {
    return this.cService.userProfile.distributorUserCompany;
  }

  get loggedInCompany() {
    return this.cService.userProfile.company;
  }

  fromMinDate = moment().startOf('day');
  toMinDate = moment().startOf('day');

  constructor(
    public dialogRef: MatDialogRef<CustomContentAdminComponent>,
    public cService: CommonService,
    public reportService: ReportsService,
    public cContentService: CustomContentService,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public misNotification: any
  ) {
    this.contentType = this.cContentService.currentContentType;
    this.isTooltipContent = this.contentType === this.cContentService.contentTypes.tooltips;

    const requiredOnAllContents = ['message', 'displayFrom'];
    this.formRequiredFields = this.isTooltipContent
      ? [...requiredOnAllContents, 'menuID']
      : [...requiredOnAllContents, 'subject', 'displayTo'];
  }

  ngOnInit() {
    moment.tz.setDefault('Etc/UTC');
    this.initForm();
    this.configureTabsAndDropdowns();
    this.listenRequiredEvents();
    if (this.misNotification) {
      this.loadMISNotification();
      console.log('%cMIS NOTIFICATION?', 'color: lime', this.misNotification);
    }

    if (this.isTooltipContent) {
      this.flattenVrmNavigationData();
    }
  }

  dateValidation() {
    return (formControl) => {
      let dateValidationErrors = null;

      if (formControl.value) {
        const selectedDate = moment(formControl.value);

        if (!selectedDate.isValid()) {
          dateValidationErrors = { invalidDate: true };
        }
      }

      return dateValidationErrors;
    };
  }

  datePrecendenceValidator(formGroup: AbstractControl) {
    let dateValidatorData = null;

    const fromDate = formGroup.get('displayFrom').value;
    const toDate = formGroup.get('displayTo').value;

    if (moment(fromDate).isAfter(moment(toDate))) {
      dateValidatorData = {
        minGreaterThanMaxError: 'Display From date is greater than Display To date.',
      };
    }

    return dateValidatorData;
  }

  loadMISNotification() {
    this.variants.clear();

    // patch message
    this.message.patchValue(this.misNotification.message);

    // patch variants
    this.misNotification.variants.forEach((variant) => {
      variant.language = this.cService.getLanguageByCode(variant.code).country;
      const newVariant: UntypedFormGroup = this.fb.group({
        id: this.fb.control(variant.id),
        messageID: this.fb.control(variant.messageID),
        language: this.fb.control(variant.language),
        code: variant.code,
        subject: this.fb.control(variant.subject),
        message: this.fb.control(variant.message),
      });

      this.variants.push(newVariant);
      if (this.availableVariants.controls[variant.language.toLowerCase()]) {
        this.availableVariants.controls[variant.language.toLowerCase()].setValue(true);
      }
    });

    if (
      this.contentType === this.cContentService.contentTypes.tooltips &&
      this.misNotification.tooltips &&
      this.misNotification.tooltips.length
    ) {
      this.tooltips.clear();

      this.misNotification.tooltips.forEach((tooltip: any) => {
        this.addTooltipToPage(tooltip.id, tooltip.messageID, tooltip.menuID, tooltip.onTitle, tooltip.onMenu);
      });
    }

    // patch distributors
    this.distributors.patchValue(this.misNotification.distributors);

    // patch companies
    this.misContentForm.controls.allCompanies.patchValue(this.misNotification.allCompanies);

    if (this.isDistributorUser && this.misNotification.allCompanies) {
      this.selectedCompanies = this.cService.userProfile.distributorCompanies.map((value, index) => {
        return { company: value };
      });
      this.misNotification.companies = this.selectedCompanies;
      this.companies.patchValue(this.selectedCompanies);
    } else if (this.isDistributorUser || (!this.isDistributorUser && this.isForloggedInCompanyOnly())) {
      this.companies.patchValue(this.misNotification.companies);
      const selectedCompanies: any = [];
      this.misNotification.companies.forEach((company) => {
        selectedCompanies.push(company);
      });
      this.selectedCompanies = selectedCompanies;
    }

    // Object.keys(this.misNotification.locations).forEach((locationName) => {
    //   this.locations.controls[locationName].patchValue(this.misNotification.locations[locationName]);
    // });
    this.locations.patchValue(this.misNotification.locations);

    // patch displays
    this.displays.patchValue(this.misNotification.displays);
  }

  private isForloggedInCompanyOnly() {
    return this.misNotification.companies.length === 1 && this.misNotification.companies[0].company;
  }

  configureTabsAndDropdowns() {
    this.selectedTabIndex = TabIndices.CREATE;

    this.companyDropdownSettings = {
      singleSelection: false,
      text: this.cService.readyTranslations.generic.select,
      selectAllText: this.cService.readyTranslations.generic.selectAll,
      unSelectAllText: this.cService.readyTranslations.generic.unselectAll,
      badgeShowLimit: 3,
      enableSearchFilter: true,
      classes: 'multiselect-dropdown',
      labelKey: 'company',
      primaryKey: 'company',
    };

    this.locationDropdownSettings = {
      singleSelection: false,
      text: this.cService.readyTranslations.generic.select,
      selectAllText: this.cService.readyTranslations.generic.selectAll,
      unSelectAllText: this.cService.readyTranslations.generic.unselectAll,
      badgeShowLimit: 3,
      enableSearchFilter: true,
      classes: 'multiselect-dropdown',
      labelKey: 'locationValue',
      primaryKey: 'locationValue',
      enableCheckAll: false,
    };

    // readonly input for Non-Distributor User
    if (!this.isDistributorUser || !this.isDistributorCompany) {
      this.allowedCompanies = [{ company: this.loggedInCompany }];
      this.selectedCompanies = [{ company: this.loggedInCompany }];
      this.companies.setValue(this.selectedCompanies);
      this.companyDropdownSettings.disabled = true;
      this.addLocations(this.loggedInCompany);
    }
    // Angular2 Multiselect formatted input for Distributor user.
    else {
      this.allowedCompanies = this.cService.userProfile.distributorCompanies.map((value, index) => {
        return { company: value };
      });
    }
  }

  // Build Form
  initForm() {
    const displayFromValidations = this.formRequiredFields.includes('displayFrom')
      ? [Validators.required, this.dateValidation()]
      : [this.dateValidation()];

    const displayToValidations = this.formRequiredFields.includes('displayTo')
      ? [Validators.required, this.dateValidation()]
      : [this.dateValidation()];

    this.misContentForm = this.fb.group({
      tooltips: this.fb.array([]),

      message: this.fb.group({
        id: this.fb.control(null),
        subject: this.fb.control(null, {
          validators: this.formRequiredFields.includes('subject') ? Validators.required : null,
        }),
        message: this.fb.control(null, {
          validators: this.formRequiredFields.includes('message') ? Validators.required : null,
        }),
        messageLevel: this.fb.control(messageLevel.COMPANY, { validators: Validators.required }),
        contentType: this.fb.control(this.contentType),
        status: this.fb.control(NotificationStatus.PENDING, { validators: Validators.required }),
      }),

      availableVariants: this.fb.group({}),
      variants: this.fb.array([]),

      distributors: this.fb.group({
        id: this.fb.control(null),
        messageID: this.fb.control(null),
        name: this.fb.control(this.cService.userProfile.distributorName, { validators: Validators.required }),
      }),
      allCompanies: this.fb.control(false),
      companies: this.fb.control(null, { validators: Validators.required }),
      locations: this.fb.group({}),

      displays: this.fb.group({
        id: this.fb.control(null),
        messageID: this.fb.control(null),
        displayIn: this.fb.control(DisplayIn.VRM_MIS, { validators: Validators.required }),
        displayAs: this.fb.control(DisplayAs.POST_LOGIN, { validators: Validators.required }),
        schedules: this.fb.group(
          {
            id: this.fb.control(null),
            displayPlatformID: this.fb.control(null),
            displayFrom: this.fb.control(null, { validators: Validators.compose(displayFromValidations) }),
            displayTo: this.fb.control(null, { validators: Validators.compose(displayToValidations) }),
          },
          { validator: this.datePrecendenceValidator }
        ),
      }),
    });

    this.addTooltipToPage();
    this.addVariant('English');

    for (const language of this.languageVariants) {
      if (language.country.toLowerCase() != this.cContentService.defaultLanguage.country) {
        this.availableVariants.addControl(language.country.toLowerCase(), this.fb.control(''));
      }
    }
  }

  // all events
  listenRequiredEvents() {
    this.companyChangeSubscription = this.companies.valueChanges.pipe(debounceTime(1000)).subscribe({
      next: (
        companies: [
          {
            company: string;
          }
        ]
      ) => {
        if (companies != null && companies.length == 1) {
          this.addLocations(companies[0].company);
        } else if (companies && companies.length != 1) {
          this.removeLocations();
        }

        if (
          companies != null &&
          this.isDistributorUser &&
          companies.length === this.cService.userProfile.distributorCompanies.length
        ) {
          this.misContentForm.controls.allCompanies.setValue(true);
        } else {
          this.misContentForm.controls.allCompanies.setValue(false);
        }
      },
    });
  }

  // create specifics

  addRemoveVariant($event: MatCheckboxChange) {
    $event.checked ? this.addVariant($event.source.value) : this.removeVariant($event.source.value);
  }

  addVariant(language: string) {
    console.log(language);
    const newVariant: UntypedFormGroup = this.fb.group({
      id: this.fb.control(null),
      messageID: this.fb.control(null),
      language: this.fb.control(language),
      code: this.cService.getLanguageByCountry(language.toLowerCase()).code,
      subject: this.fb.control(''),
      message: this.fb.control(''),
    });

    this.variants.push(newVariant);

    const subjectValidations = this.formRequiredFields.includes('subject') ? [Validators.required] : [];
    const messageValidations = this.formRequiredFields.includes('message') ? [Validators.required] : [];

    newVariant.controls.language.setValidators([Validators.required]);
    newVariant.controls.subject.setValidators(subjectValidations);
    newVariant.controls.message.setValidators(messageValidations);
  }

  removeVariant(language: string) {
    let index = 0;
    for (const variant of this.variants.controls) {
      if (variant.value.language.toLowerCase() === language.toLowerCase()) {
        this.variants.removeAt(index);
        continue;
      }
      index++;
    }
  }

  // preview specifics

  previewNotification(language) {
    if (language.toLowerCase() === this.cContentService.defaultLanguage.country) {
      this.previewContent = {
        notificationLanguage: language.toLowerCase(),
        dialogTitle: this.textTranslations.vrmNotification[language.toLowerCase()]
          ? this.textTranslations.vrmNotification[language.toLowerCase()]
          : this.textTranslations.vrmNotification['english'],
        contentTitle: this.message.value.subject,
        contentMessage: this.message.value.message,
        continueButton: this.textTranslations.continue[language.toLowerCase()]
          ? this.textTranslations.continue[language.toLowerCase()]
          : this.textTranslations.continue['english'],
      };
    } else {
      for (const variant of this.variants.controls) {
        if (variant.value.language.toLowerCase() === language.toLowerCase()) {
          this.previewContent = {
            notificationLanguage: language.toLowerCase(),
            dialogTitle: this.textTranslations.vrmNotification[language.toLowerCase()]
              ? this.textTranslations.vrmNotification[language.toLowerCase()]
              : this.textTranslations.vrmNotification['english'],
            contentTitle: variant.value.subject,
            contentMessage: variant.value.message,
            continueButton: this.textTranslations.continue[language.toLowerCase()]
              ? this.textTranslations.continue[language.toLowerCase()]
              : this.textTranslations.continue['english'],
          };
          break;
        }
      }
    }
  }

  // Audience specifics

  addLocations(selectedCompany?: string) {
    if (selectedCompany == null) {
      selectedCompany = this.companies.value;
    }
    if (this.companySpecificLocations == null) {
      this.cContentService.getCompanyLocationFiltersForAudience(selectedCompany).subscribe({
        next: (locations: any) => {
          if (this.selectedCompanies != null && this.selectedCompanies.length == 1) {
            this.companySpecificLocations = locations;

            for (const location of this.companySpecificLocations) {
              this.locations.addControl(location.colName, this.fb.control(''));

              location.data =
                location.filters != null
                  ? location.filters.map((value, index) => {
                      return { locationValue: value.value };
                    })
                  : null;

              // load selected for edit
              if (
                this.misNotification &&
                selectedCompany === this.misNotification.companies[0].company &&
                this.misNotification.locations &&
                this.misNotification.locations[location.colName]
              ) {
                this.locations.controls[location.colName].setValue(this.misNotification.locations[location.colName]);
                location.selected = this.misNotification.locations[location.colName];
              }
            }
          }
        },
      });
    }
  }

  removeLocations() {
    if (this.companySpecificLocations != null) {
      for (const location of this.companySpecificLocations) {
        this.locations.removeControl(location.colName);
      }
      this.companySpecificLocations = null;
    }
  }

  // Tooltips
  addTooltipToPage(
    id: any = null,
    messageID: any = null,
    menuID: string = null,
    onTitle: boolean = false,
    onMenu: boolean = false
  ) {
    const tooltipFormGroup = this.fb.group({
      id: this.fb.control(id),
      messageID: this.fb.control(messageID),
      menuID: this.fb.control(menuID, Validators.required),
      onTitle: this.fb.control(onTitle),
      onMenu: this.fb.control(onMenu),
    });

    this.tooltips.push(tooltipFormGroup);
  }

  removeTooltipPage(index: number) {
    this.tooltips.removeAt(index);
  }

  previewTooltipInNewTab(pageNav?: PageChildNavItem) {
    if (!pageNav) {
      pageNav = this.availableTooltipPreviews[0];
    }

    localStorage.setItem(
      this.cService.localStorageKeys.pageTooltipPreviews,
      this.cService.localStorageKeys.pageTooltipPreviews
    );

    window.open(pageNav.url, '_blank');
  }

  private collateAvailableTooltipPreviews() {
    if (this.tooltips.value && this.tooltips.value.length) {
      this.availableTooltipPreviews = [];

      this.tooltips.value.forEach((tooltip: { menuID: string }) => {
        const tTip = this.availableVrmPageList.find((page) => page.id === tooltip.menuID);
        if (tTip) {
          this.availableTooltipPreviews.push(tTip);
        }
      });

      console.log('%cAVAILABLE T-TIP PREVIEW PAGES', 'color: lime', this.availableTooltipPreviews);
    }
  }

  private manuallyValidateTooltipPlacementValues(): boolean {
    let valid: boolean = true;

    if (this.tooltips.controls.length && this.contentType === this.cContentService.contentTypes.tooltips) {
      for (let i = 0; i < this.tooltips.controls.length; i++) {
        if (
          !this.tooltips.controls[i]['controls'].onTitle.value &&
          !this.tooltips.controls[i]['controls'].onMenu.value
        ) {
          valid = false;
        }
      }
    }

    return valid;
  }

  // tab traversals

  goNextTab() {
    if (this.selectedTabIndex === TabIndices.SCHEDULE) {
      this.closeDialog();
    }
    this.selectedTabIndex++;

    if (this.contentType === this.cContentService.contentTypes.tooltips) {
      this.collateAvailableTooltipPreviews();
    }
    console.log('%cFORM VALUES', 'color: salmon', this.misContentForm.value);
  }

  goPreviousTab() {
    this.selectedTabIndex--;
  }

  // validation and savings

  saveNotificationVariants() {
    this.message.controls.subject.setValue(this.variants.controls[0]['controls']['subject'].value);
    this.message.controls.message.setValue(this.variants.controls[0]['controls']['message'].value);

    this.message.markAllAsTouched();
    this.message.updateValueAndValidity();

    this.variants.markAllAsTouched();
    this.variants.updateValueAndValidity();

    if (this.message.valid && this.variants.valid) {
      const variantsPreviewStillAvailable =
        !!this.previewContent &&
        this.previewContent.notificationLanguage.toLowerCase() != this.cContentService.defaultLanguage.country &&
        !!this.availableVariants.controls[this.previewContent.notificationLanguage.toLowerCase()].value;

      if (variantsPreviewStillAvailable) {
        this.previewNotification(this.previewContent.notificationLanguage.toLowerCase());
      } else {
        this.previewNotification(this.cContentService.defaultLanguage.country);
      }

      this.applyManualValidationBeforeSave();
    }
  }

  saveAudience() {
    this.companies.markAllAsTouched();
    this.companies.updateValueAndValidity();

    if (this.companies.valid) {
      this.applyManualValidationBeforeSave();
    }
  }

  setNotification() {
    this.displays.markAllAsTouched();
    this.displays.updateValueAndValidity();

    if (this.displays.valid) {
      this.message.controls['status'].setValue(NotificationStatus.ACTIVE);
      this.applyManualValidationBeforeSave();
    }
  }

  private applyManualValidationBeforeSave() {
    if (this.contentType === this.cContentService.contentTypes.tooltips) {
      const onTitleOrMenuSpecified = this.manuallyValidateTooltipPlacementValues();

      if (onTitleOrMenuSpecified) {
        this.saveOrUpdateContent();
      }
    } else {
      this.saveOrUpdateContent();
    }
  }

  private saveOrUpdateContent() {
    if (this.message.controls['id'].value != null) {
      const id = this.message.controls['id'].value;
      this.cContentService.updateContent(id, this.misContentForm.value).subscribe({
        next: (misNotification: any) => {
          this.patchNotification(misNotification);
        },
      });
    } else {
      this.cContentService.saveContent(this.misContentForm.value).subscribe({
        next: (misNotification: any) => {
          this.patchNotification(misNotification);
        },
      });
    }
    this.cContentService.updateMenuBadgeCount();
  }

  patchNotification(misNotification) {
    // patch message
    this.message.patchValue(misNotification.message);

    // patch variants
    misNotification.variants.forEach((variant) => {
      variant.language = this.cService.getLanguageByCode(variant.code).country;
      const variantFormGroup =
        this.variants.controls[
          this.variants.controls.findIndex((control) => {
            return control.value.language === variant.language;
          })
        ];
      variantFormGroup.patchValue(variant);
    });

    // patch distributors
    this.distributors.patchValue(misNotification.distributors);

    // patch companies
    if (!misNotification.allCompanies) {
      this.companies.patchValue(misNotification.companies);
    }

    // patch locations
    this.locations.patchValue(misNotification.locations);

    // patch displays
    this.displays.patchValue(misNotification.displays);

    this.goNextTab();
  }

  // dialog specifics
  closeDialog() {
    this.misContentForm.reset();
    this.misNotification = null;
    this.cContentService.updateMenuBadgeCount();
    this.dialogRef.close();
  }

  private flattenVrmNavigationData() {
    this.cService.navigation.forEach((nav: Navigation) => {
      if (nav) {
        if (!nav.children) {
          this.availableVrmPageList.push(nav);
        } else {
          nav.children.forEach((navChild: PageChildNavItem) => {
            navChild.parentId = nav.id;
            navChild.parentName = nav.name;
            navChild.parentTranslatedName = nav.translatedName;
            navChild.isChild = true;

            this.availableVrmPageList.push(navChild);
          });
        }
      }
    });
  }

  ngOnDestroy() {
    this.misContentForm.reset();
    this.misNotification = null;

    if (this.companyChangeSubscription) {
      this.companyChangeSubscription.unsubscribe();
    }
  }
}
